import { Phone } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import type { AdsMode } from "./AdsModeSelector";

const DEMO_NUMBER = "05 17 94 84 03";
const DEMO_TEL = "tel:+33517948403";

const titles = {
  vente: "Appelez. Jugez.",
  location: "Essayez par vous même.",
};

const subtitles = {
  vente: "C'est exactement ce que vos prospects entendent quand vous ne décrochez pas.",
  location: "C'est exactement ce qu'un candidat locataire entend quand il appelle.",
};

const AdsCallTest = ({ mode }: { mode: AdsMode }) => {
  const containerRef = useScrollReveal({});

  const handleCallClick = () => {
    trackEvent("lp_offre2026_appel_demo", {
      event_category: "lp_offre_2026",
      location: "call_test_section",
      phone_number: DEMO_NUMBER,
    });
  };

  return (
    <section id="ads-call-test" className="section-padding bg-secondary">
      <div className="container mx-auto px-4">
        <div ref={containerRef} className="max-w-xl mx-auto">
          <div
            className="scroll-reveal-item card-stripe p-8 md:p-10 text-center border-primary/20"
            data-scroll-item
          >
            <div className="flex items-center justify-center gap-2 mb-6">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                </span>
                En direct
              </span>
            </div>
            <div className="relative w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6 ring-4 ring-primary/20">
              <span className="absolute inset-0 rounded-full bg-primary/10 animate-ping opacity-30" />
              <Phone className="w-10 h-10 text-primary relative z-10" />
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
              {titles[mode]}
            </h2>
            <p className="text-muted-foreground mb-6 text-lg">
              {subtitles[mode]}
            </p>

            <ul className="text-sm text-foreground/80 space-y-2 mb-8 text-left inline-block">
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                Disponible 24h/24, même le dimanche à 21h
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                Votre numéro de ligne reste inchangé
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                Le prospect a une réponse immédiate — pas une boîte vocale
              </li>
            </ul>

            <a
              href={DEMO_TEL}
              onClick={handleCallClick}
              className="inline-flex items-center gap-3 px-6 py-4 md:px-8 md:py-5 bg-primary text-primary-foreground rounded-full text-xl md:text-3xl font-bold shadow-warm-lg hover:shadow-warm hover:bg-primary/90 transition-all hover:-translate-y-0.5 active:translate-y-0"
            >
              <Phone className="w-7 h-7" />
              {DEMO_NUMBER}
            </a>

            <p className="text-xs text-muted-foreground mt-4">
              Appel réel — aucune inscription · Numéro local non surtaxé
            </p>

            <p className="text-sm text-foreground/80 mt-6 font-medium">
              À la fin de l'appel, une fiche prospect est générée : intention, budget, coordonnées, résumé. Prête à être utilisée pour un rappel.
            </p>
          </div>

          <div className="flex flex-col items-center mt-10 animate-fade-in">
            <div className="w-px h-10 bg-gradient-to-b from-primary/40 to-primary/10" />
            <p className="text-sm font-medium text-primary mt-3">
              Voici ce que ça donne concrètement ↓
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdsCallTest;
